/**
 * Placement maths for a floating panel pinned to an anchor element — the
 * caret autocompletes (`MentionDropdown`, `EmojiSuggestionDropdown`) and
 * anything else that renders into a portal with `position: fixed`.
 *
 * Kept as a pure function of two rects on purpose: the interesting part is
 * "does it fit, and if not what gives", which is exactly the part that was
 * wrong on iPhone (OSO-380) and exactly the part that's untestable once it's
 * tangled up with refs and effects. The hook wrapper lives in
 * `lib/hooks/useAnchoredPosition.ts`.
 */

export type Placement = "above" | "below";

/** A DOMRect-shaped input — only the fields we actually read. */
export interface Rect {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface AnchoredPosition {
  placement: Placement;
  /** `position: fixed` offsets, i.e. client coordinates. */
  top: number;
  left: number;
  width: number;
  /**
   * The most the panel may render at: the space actually available on the
   * chosen side, never `desiredHeight` itself.
   *
   * BELOW, that is simply the room between the anchor and the bottom edge, so
   * the cap only binds when the viewport bites — a panel whose content is
   * taller than the caller's estimate grows to its natural height on a tall
   * screen instead of scrolling inside the estimate (OSO-516: every profile
   * card was capped at the 260px guess, on a 1080p desktop included).
   *
   * ABOVE, the panel is hung from `top` and grows DOWNWARD, so the only box
   * whose bottom edge meets the anchor is one exactly as tall as the height
   * `top` was computed from. There the cap is `min(desiredHeight, available)`;
   * anything taller would either overlap the anchor or float away from it.
   * A caller whose real height can exceed its estimate should prefer "below".
   */
  maxHeight: number;
}

/** Gap between the anchor and the panel. */
const GAP = 4;
/**
 * Kept clear of every viewport edge, so the panel never sits flush.
 *
 * Exported because a panel that caps its OWN width has to cap it at the same
 * number: `left` is clamped for a box `width` wide, and a box drawn wider than
 * that is one this file cannot hold on screen however carefully it places the
 * left edge (OSO-572).
 */
export const VIEWPORT_MARGIN = 8;
/**
 * A panel shorter than this is useless (less than one row plus its padding),
 * so in a viewport too cramped for even that we overflow deliberately and let
 * the final clamp decide which edge wins, rather than rendering a sliver.
 * The default for `minHeight`; a panel that knows its own height overrides it.
 */
const MIN_HEIGHT = 96;

/**
 * The visible band, in the same client coordinates `getBoundingClientRect`
 * and `position: fixed` use.
 *
 * `window.innerHeight` is the wrong number on iOS: it reports the *layout*
 * viewport, which does not shrink when the software keyboard opens. The
 * visual viewport does, and `offsetTop` is where it starts inside the layout
 * viewport — so this is what "on screen" actually means with a keyboard up.
 */
export function visualViewportRect(): Rect {
  const vv = typeof window !== "undefined" ? window.visualViewport : undefined;
  if (vv) {
    return {
      top: vv.offsetTop,
      bottom: vv.offsetTop + vv.height,
      left: vv.offsetLeft,
      width: vv.width,
    };
  }
  return {
    top: 0,
    bottom: typeof window === "undefined" ? 0 : window.innerHeight,
    left: 0,
    width: typeof window === "undefined" ? 0 : window.innerWidth,
  };
}

export function computeAnchoredPosition({
  anchor,
  viewport,
  preferred = "above",
  desiredHeight,
  minHeight = MIN_HEIGHT,
  align = "start",
  maxWidth,
  width: fixedWidth,
}: {
  anchor: Rect;
  viewport: Rect;
  /** Side to use when the panel fits on both. */
  preferred?: Placement;
  /** How tall the panel would like to be given its content. */
  desiredHeight: number;
  /**
   * Floor on the reserved height, overriding `MIN_HEIGHT`.
   *
   * That floor is a list panel's: less than one row plus padding is not worth
   * rendering. A panel that KNOWS its height — a hover tooltip is two lines and
   * ~40px — must pass it, or the floor reserves a box three times its size and
   * hangs it that far off the anchor.
   */
  minHeight?: number;
  /**
   * Horizontal relationship to the anchor: left edges lined up (`start`, what
   * a caret autocomplete wants) or centred on it (`center`, what a tooltip
   * over a small chip wants). Both still clamp to the viewport, which is the
   * whole reason a centred tooltip comes here instead of using
   * `-translate-x-1/2`.
   */
  align?: "start" | "center";
  /** Caps the panel's width independently of the anchor's. */
  maxWidth?: number;
  /**
   * The panel's OWN width, for panels that do not take the anchor's.
   *
   * The caret autocompletes match the width of what they are completing, so
   * they leave this alone. A profile hover card does not — it is a fixed-width
   * card hanging off a name that might be four characters long, and deriving
   * its width from the anchor would both size it wrong and clamp its left edge
   * against a width it does not have.
   *
   * Distinct from `maxWidth`, which caps an anchor-derived width; this one
   * replaces it. Both still yield to the viewport.
   */
  width?: number;
}): AnchoredPosition {
  const spaceAbove = anchor.top - GAP - (viewport.top + VIEWPORT_MARGIN);
  const spaceBelow = viewport.bottom - VIEWPORT_MARGIN - (anchor.bottom + GAP);

  // Flip only when the preferred side can't hold the panel AND the other side
  // is genuinely better — flipping into an equally cramped side just moves the
  // problem and makes the panel jump around as the user types.
  const fits = preferred === "above" ? spaceAbove >= desiredHeight : spaceBelow >= desiredHeight;
  const other = preferred === "above" ? spaceBelow : spaceAbove;
  const own = preferred === "above" ? spaceAbove : spaceBelow;
  const placement: Placement = fits || other <= own ? preferred : preferred === "above" ? "below" : "above";

  const available = placement === "above" ? spaceAbove : spaceBelow;
  // See `AnchoredPosition.maxHeight` for why the two sides differ.
  const height = Math.max(minHeight, Math.min(desiredHeight, available));
  const maxHeight = placement === "above" ? height : Math.max(minHeight, available);

  const width = Math.max(
    0,
    Math.min(fixedWidth ?? anchor.width, maxWidth ?? Infinity, viewport.width - VIEWPORT_MARGIN * 2),
  );

  const minLeft = viewport.left + VIEWPORT_MARGIN;
  const rawLeft = align === "center" ? anchor.left + anchor.width / 2 - width / 2 : anchor.left;
  const left = clamp(rawLeft, minLeft, Math.max(minLeft, viewport.left + viewport.width - VIEWPORT_MARGIN - width));

  const minTop = viewport.top + VIEWPORT_MARGIN;
  const rawTop = placement === "above" ? anchor.top - GAP - height : anchor.bottom + GAP;
  // Last line of defence. `maxHeight` already fits `available` in the normal
  // case; this catches the cramped one, where the height floor won and the
  // panel would otherwise hang off an edge — which is the whole bug.
  const top = clamp(rawTop, minTop, Math.max(minTop, viewport.bottom - VIEWPORT_MARGIN - maxHeight));

  return { placement, top, left, width, maxHeight };
}

/**
 * The box an anchored popover must stay inside: the nearest ancestor carrying
 * `data-popover-bounds`, or the visible viewport when there is none.
 *
 * That attribute marks a box a popover cannot escape — a scrolling or
 * otherwise clipping container, such as the chat dock's message list or the
 * lightbox sidebar. It is written on those elements by hand rather than
 * inferred from "the nearest scrollable ancestor", because scroll containers
 * are everywhere and inferring the bound would silently move a popover the
 * day somebody adds an `overflow` class three levels up.
 *
 * Everything outside such a container falls through to the viewport, so an
 * ordinary feed popover is clamped by the window exactly as before.
 */
export function popoverBoundsRect(el: Element | null | undefined): Rect {
  const container = el?.closest("[data-popover-bounds]");
  if (!container) return visualViewportRect();
  const { top, bottom, left, width } = container.getBoundingClientRect();
  return { top, bottom, left, width };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
